import { useFormContext, useFormState } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import ArrowIcon from '@/assets/icons/arrow-right.svg?react'
import WarningCircleIcon from '@/assets/icons/warning-circle.svg?react'
import Checkbox from '@/components/Checkbox/Checkbox'
import FormField from '@/components/FormField'
import InputNumber from '@/components/InputNumber'
import Label from '@/components/Label'
import type { FormFieldType } from '@/types/form-field-type'
import { isEven } from '@/utils/is-even'

import styles from './BasketCourseSemesterArraySection.module.scss'
import { SEMESTERS } from '../constants/curriculum-basket-config'
import type { CurriculumForm } from '../hooks/useCurriculumForm'

type BasketCourseSemesterArraySectionProps = {
  basketIndex: number
  courseIndex: number
}

const BasketCourseSemesterArraySection = (
  props: BasketCourseSemesterArraySectionProps
) => {
  const { t } = useTranslation('curriculumsManagement')
  const form = useFormContext<CurriculumForm>()

  const timeUnit = form.watch(
    `baskets.${props.basketIndex}.courses.${props.courseIndex}.timeUnit`
  )

  const isHoursPerYear = timeUnit === 'year'
  const visibleFields = isHoursPerYear ? SEMESTERS.filter(isEven) : SEMESTERS

  return (
    <div className={styles.container}>
      <Label
        className={styles.label}
        id={`baskets.${props.basketIndex}.courses.${props.courseIndex}.semesters`}
        required
        label={
          <Trans
            t={t}
            i18nKey="label.hours-per"
            values={{ TIME_UNIT: timeUnit }}
          />
        }
      />
      {visibleFields.map(semesterIndex => (
        <SemesterSlot
          key={semesterIndex}
          semesterIndex={semesterIndex}
          semestersShortened={isHoursPerYear}
          {...props}
        />
      ))}
      <SemestersError {...props} />
    </div>
  )
}

type SemestersErrorProps = BasketCourseSemesterArraySectionProps

const SemestersError = (props: SemestersErrorProps) => {
  const { errors } = useFormState<CurriculumForm>({
    name: `baskets.${props.basketIndex}.courses.${props.courseIndex}.semesters`,
    exact: true
  })

  const semestersErrors =
    errors?.baskets?.[props.basketIndex]?.courses?.[props.courseIndex]
      ?.semesters

  const errorMessage = semestersErrors?.find?.(v => !!v?.hours?.message)?.hours
    ?.message

  if (!errorMessage) return null

  return (
    <p className={styles.error} aria-hidden="true">
      <WarningCircleIcon aria-hidden="true" />
      {errorMessage}
    </p>
  )
}

type SemesterSlotProps = {
  semestersShortened: boolean
  basketIndex: number
  courseIndex: number
  semesterIndex: number
}

const SemesterSlot = (props: SemesterSlotProps) => {
  const { t } = useTranslation('curriculumsManagement')
  const form = useFormContext<CurriculumForm>()

  const baseName =
    `baskets.${props.basketIndex}.courses.${props.courseIndex}.semesters.${props.semesterIndex}` as const

  const ordinal = props.semesterIndex + 1

  const isActive = form.watch(`${baseName}.isActive`)

  const createBlurValidationHandler =
    <FieldValue,>(
      inputProps: FormFieldType<FieldValue>
    ): FormFieldType<FieldValue>['onBlur'] =>
    (...args) => {
      inputProps.onBlur?.(...args)

      const semestersName =
        `baskets.${props.basketIndex}.courses.${props.courseIndex}.semesters` as const
      form.trigger(semestersName)

      SEMESTERS.forEach(i => {
        const semesterName = `${semestersName}.${i}` as const

        form.trigger(`${semesterName}.isActive`)
        form.trigger(`${semesterName}.hours`)
      })
    }

  const createIsActiveChangeHandler =
    <FieldValue,>(
      inputProps: FormFieldType<FieldValue>
    ): FormFieldType<FieldValue>['onChange'] =>
    value => {
      inputProps.onChange?.(value)

      form.setValue(`${baseName}.hours`, null)
    }

  const createHoursChangeHandler =
    <FieldValue,>(
      inputProps: FormFieldType<FieldValue>
    ): FormFieldType<FieldValue>['onChange'] =>
    value => {
      inputProps.onChange?.(value)

      form.setValue(`${baseName}.isActive`, !!value)
    }

  return (
    <div className={styles.row}>
      <FormField
        id={`${baseName}.isActive`}
        name={`${baseName}.isActive`}
        control={form.control}
        hideError
        render={({ inputProps }) => (
          <Checkbox
            {...inputProps}
            label={
              props.semestersShortened
                ? t('label.n-semester-short', {
                    FIRST: ordinal,
                    SECOND: props.semesterIndex + 2
                  })
                : t('label.n-semester', { ORDINAL: ordinal })
            }
            onChange={createIsActiveChangeHandler(inputProps)}
            onBlur={createBlurValidationHandler(inputProps)}
          />
        )}
      />
      <ArrowIcon className={styles.icon} aria-hidden="true" />
      <FormField
        id={`${baseName}.hours`}
        name={`${baseName}.hours`}
        control={form.control}
        label={
          props.semestersShortened
            ? t('label.n-number-of-hours-short', {
                FIRST: ordinal,
                SECOND: props.semesterIndex + 2
              })
            : t('label.n-number-of-hours', {
                ORDINAL: ordinal
              })
        }
        hideLabel
        required={isActive}
        hideError
        render={({ inputProps }) => (
          <InputNumber
            {...inputProps}
            placeholder={t('placeholder.type-number-of-hours')}
            onChange={createHoursChangeHandler(inputProps)}
            onBlur={createBlurValidationHandler(inputProps)}
          />
        )}
      />
    </div>
  )
}

export default BasketCourseSemesterArraySection
